import type { ComponentProps } from "react";

import { ReviewCardTemplate, ReviewContent } from "./card-template";
import type { Review } from "./common";

import { StarRatingScale } from "~/entities/review";
import { getFullName, UserAvatar } from "~/entities/user";
import { Tk, useTranslation2 } from "~/shared/i18n";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "~/shared/ui/dialog";
import { Time } from "~/shared/ui/time";

type Props = Omit<
  ComponentProps<typeof ReviewCardTemplate>,
  "avatar" | "fullName" | "createdAt" | "ratingScale" | "reviewContent"
> & {
  review: Omit<Review, "id" | "customer" | "isApproved" | "orderNumber"> & {
    customer: Omit<Review["customer"], "email" | "phone">;
  };
};

function ReviewCard({
  review: { rating, pros, cons, text, customer, createdAt },
  ...props
}: Props) {
  const {
    postproc: { tc },
  } = useTranslation2();

  const fullName = <span className="truncate">{getFullName(customer)}</span>;
  const avatar = <UserAvatar user={customer} />;
  const reviewContent = {
    pros,
    cons,
    text,
  };

  return (
    <Dialog>
      <DialogTrigger asChild>
        <button type="button" className="text-start">
          <ReviewCardTemplate
            avatar={avatar}
            fullName={fullName}
            createdAt={
              <Time dateTime={createdAt} className="text-muted-foreground text-sm font-normal" />
            }
            ratingScale={<StarRatingScale rating={rating} />}
            reviewContent={reviewContent}
            className="h-full cursor-pointer hover:shadow-lg"
            {...props}
          />
        </button>
      </DialogTrigger>
      <DialogContent className="flex max-h-4/5 flex-col overflow-hidden sm:max-w-4/5">
        <DialogHeader>
          <DialogTitle>{tc(Tk.REVIEW_DETAILS)}</DialogTitle>
          <DialogDescription>
            <StarRatingScale
              rating={rating}
              starProps={{
                className: "sm:size-8",
              }}
            />
          </DialogDescription>
        </DialogHeader>
        <ReviewContent data={reviewContent} className="overflow-auto max-sm:text-xs" />
        <DialogFooter className="xs:flex-row xs:items-center flex-col justify-between max-sm:text-xs sm:justify-between">
          <div className="flex min-w-0 items-center gap-2">
            <UserAvatar user={customer} className="max-sm:size-8" />
            {fullName}
          </div>
          <Time dateTime={createdAt} className="max-xs:text-end text-nowrap" />
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export { ReviewCard };
